import { chain, concat, difference, filter, get, isArray, isEmpty, map, range, size } from 'lodash';
import { mapActions, mapState } from 'pinia';
import Targets from 'src/mixins/Targets';
import { programsTargets } from 'src/store/programsTargets';
import { promptLevels as promptLevelsStore } from 'src/store/promptLevels';
import { workflows } from 'src/store/workflows';
import { isBehaviorOccurrence } from 'src/utils/behaviorOccurrences';
import { logEvent } from 'src/utils/LogEvent';
import { formatDuration } from 'src/utils/DurationUtils';
import { formatRate, getTargetMetric, getTargetType } from 'src/utils/TargetUtils';
import { OUTCOME_MEASUREMENT_TYPES } from 'src/constants/prompt-level-template';
import { formatRatingScore } from 'src/utils/PromptLevelUtils';

export default {
  mixins: [
    Targets,
  ],

  computed: {
    ...mapState(promptLevelsStore, ['promptLevelTemplates']),
    ...mapState(workflows, ['workflowStatuses']),
  },

  methods: {
    ...mapActions(programsTargets, ['getAllProgramTargets']),

    formatDataResult(targetDataTarget, program) {
      const { aggregatedData, promptLevelTemplate: promptLevelTemplateId } = targetDataTarget;

      let promptLevelTemplate = this.promptLevelTemplates?.[promptLevelTemplateId];
      if (promptLevelTemplate?.outcomeMeasurement === OUTCOME_MEASUREMENT_TYPES.RATING_SCALE) {
        return formatRatingScore(aggregatedData?.percentage);
      }

      let { dataType } = targetDataTarget;
      // @todo This is a bit dirty. Refactor it when we no longer use program data types
      if (['duration', 'frequency'].includes(program.type)) {
        dataType = program.type;
      }

      if (['rate'].includes(dataType)) {
        const metric = getTargetMetric(
          targetDataTarget.target?.primaryMetric,
          dataType,
          program.primaryMetric,
          program.type,
        );
        return formatRate(aggregatedData.successes, aggregatedData.duration, metric)
          ?? this.formatFrequency(aggregatedData.successes, aggregatedData.duration);
      }

      if (['frequency'].includes(dataType)) {
        if (aggregatedData.successes === aggregatedData.trials) {
          return aggregatedData.successes || 0;
        }
        return `${
          aggregatedData.successes || 0
        } / ${
          aggregatedData.trials || 0
        }`;
      }

      if (['duration'].includes(dataType)) {
        if (aggregatedData.successfulDuration === aggregatedData.duration) {
          return `${this.formatDuration(aggregatedData.successfulDuration, false)}`;
        }
        return `${
          this.formatDuration(aggregatedData.successfulDuration, false)
        } / ${
          this.formatDuration(aggregatedData.duration, false)
        }`;
      }

      if (aggregatedData.trials === 0) {
        return '- %';
      }

      if (!promptLevelTemplate) {
        promptLevelTemplate = {};
        /**
         * We may sometimes get into a situation when a program or its targets have a PLT from another clinic. In that
         * case, it couldn't be found in `this.promptLevelTemplates`.
         * This log was added to locate such programs/targets/PLTs.
         * @todo Remove this logging after not seeing it in Bugsnag for a while.
         */
        logEvent('No Prompt Level Template found rendering session data in notes', {
          severity: 'warning',
          context: {
            context_data: {
              program: program.id,
              target: targetDataTarget.id,
              promptLevelTemplate: promptLevelTemplateId,
            },
          },
        });
      }

      const { outcomeMeasurement, promptLevels } = promptLevelTemplate;

      const binaryResult = (aggregatedData.successes / aggregatedData.trials) * 100;
      const isPromptBinary = outcomeMeasurement === 'binary';
      const hasWeightsSet = promptLevels?.some((prompt) => prompt.score !== 100 || prompt.duration !== 0) ?? false;

      const promptResultByType = isPromptBinary && !hasWeightsSet ? binaryResult : aggregatedData.percentage;
      return `${Math.round(promptResultByType)} %`;
    },

    /**
     * The timed measurements of a target, for the AI note generator. Null when the target is not timed.
     *
     * @returns {{ rate: string, observedDuration: string } | { duration: string } | null}
     */
    getTimedTargetMeasurements(targetDataTarget, program) {
      if (!targetDataTarget.hasData) {
        return null;
      }
      const { duration } = targetDataTarget.aggregatedData;
      const targetType = getTargetType(targetDataTarget.target, program);
      if (targetType === 'rate') {
        return {
          rate: this.formatDataResult(targetDataTarget, program),
          observedDuration: formatDuration(duration),
        };
      }
      if (targetType === 'duration') {
        return { duration: formatDuration(duration) };
      }
      return null;
    }, // getTimedTargetMeasurements()

    /**
     * Format data and columns to show in Quasar table
     */
    async analyzeTargetData(session, program, data, includeAllTargets = false, selectedTargets = {}) {
      if (!includeAllTargets
        && chain(data).get('data').isEmpty().value()) {
        return [];
      }

      const sessionProgramDataWithScoringData = chain(data)
        .get('data')
        .map((dataItem) => ({
          ...dataItem,
          ...(dataItem.durationFromSession && { duration: session.duration }),
          promptLevelDetails: chain(this.promptLevelTemplates)
            .find({
              id: dataItem.promptLevelTemplate || get(program, 'promptLevelTemplate'),
            })
            .get('promptLevels')
            .find({ id: dataItem.promptLevel })
            .defaultTo(null)
            .value(),
        }))
        .value();

      let programTargets = [];

      // Load all targets for editing
      const allProgramTargets = await this.getAllProgramTargets({ program: program.id });

      const getParentTargets = (parentTargetId, targetHierarchy) => {
        if (!parentTargetId || size(targetHierarchy) > 5) {
          return targetHierarchy;
        }
        return getParentTargets(
          chain(allProgramTargets).find({ id: parentTargetId }).get('parentTarget', null).value(),
          concat(targetHierarchy, parentTargetId),
        );
      };

      let maxDepth = 0;
      let targetHierarchyIds = [];
      if (includeAllTargets) {
        const targetIdsWithData = chain(sessionProgramDataWithScoringData)
          .map('target')
          .uniq()
          .value();

        const parentTargets = allProgramTargets.reduce((acc, target) => {
          if (target.parentTarget && !(target.parentTarget in acc)) {
            acc[target.parentTarget] = allProgramTargets.find(({ id }) => id === target.parentTarget);
          }
          return acc;
        }, {});

        const targetStatusTypes = chain(allProgramTargets)
          .map((target) => {
            return [
              target.id,
              this.getTargetWorkflowDetails(
                this.workflowStatuses,
                program,
                target,
                'type',
                parentTargets[target.parentTarget],
              ),
            ];
          })
          .fromPairs()
          .value();

        // Check if target (and parent target) is open
        const isTargetOpen = (target) => {
          if (target.parentTarget) {
            if (!get(selectedTargets, get(targetStatusTypes, target.parentTarget))) {
              return false;
            }
          }
          return get(
            selectedTargets,
            get(targetStatusTypes, target.id),
            false,
          );
        };

        // Only show targets that are open or that already have data
        programTargets = filter(
          allProgramTargets,
          (target) => (
            targetIdsWithData.includes(get(target, 'id'))
              || isTargetOpen(target)
          ),
        );

        const targetDepthMap = chain(programTargets)
          .uniqBy('id')
          .map((targetInfo) => getParentTargets(targetInfo.parentTarget, [targetInfo.id]))
          .value();

        maxDepth = (chain(targetDepthMap).map((targetHierarchy) => size(targetHierarchy)).max().value() || 0) - 1;

        targetHierarchyIds = chain(targetDepthMap).flatten().uniq().value();
      } else {
        // When viewing data
        const targetDepthMap = chain(sessionProgramDataWithScoringData)
          .uniqBy('target')
          .map((targetInfo) => getParentTargets(targetInfo.parentTarget, [targetInfo.target]))
          .value();

        maxDepth = (chain(targetDepthMap).map((targetHierarchy) => size(targetHierarchy)).max().value() - 1) || 0;

        targetHierarchyIds = chain(targetDepthMap).flatten().uniq().value();

        programTargets = filter(allProgramTargets, (programTarget) => (targetHierarchyIds.includes(programTarget.id)));
      }

      if (programTargets.length === 0) {
        return [];
      }

      if (maxDepth > 0) {
        // Make sure we include parent targets even if they are not open
        const missingParentTargets = difference(targetHierarchyIds, map(programTargets, 'id'));
        programTargets = concat(
          programTargets,
          filter(allProgramTargets, (programTarget) => missingParentTargets.includes(programTarget.id)),
        );
      }

      const programPromptLevelTemplate = get(program, 'promptLevelTemplate', null);
      const maxTrial = chain(sessionProgramDataWithScoringData).maxBy('trial').get('trial').value();

      // Build a matrix of trials (horizontal axis) and programs (vertical axis)
      const mappedData = chain(programTargets)
        .compact()
        .map((programTarget) => {
          let targetOrder;
          let indent = 0;
          if (programTarget.parentTarget) {
            const parentTargetHierarchy = getParentTargets(programTarget.parentTarget, [programTarget.id]);

            targetOrder = chain(parentTargetHierarchy)
              .reverse()
              .reduce(
                (pV, programTargetId, idx) => (pV
                  + (chain(programTargets)
                    .find({ id: programTargetId })
                    .get('order', 0)
                    .value() * (10 ** ((maxDepth - idx) * 4))
                  )),
                0,
              )
              .value();
            indent = size(parentTargetHierarchy) - 1;
          } else {
            targetOrder = programTarget.order * (10 ** (maxDepth * 4));
          }

          let trials;
          if ((programTarget.dataType === 'rate') && (programTarget.timerType !== 'session')) {
            // Trials contains an array of trials (currently only rate programs)
            trials = chain(range(1, maxTrial + 1))
              .map((trial) => chain(sessionProgramDataWithScoringData)
                .filter({ target: programTarget.id, trial })
                .defaultTo([{}])
                .value())
              .value();
          } else {
            const isDurationTarget = getTargetType(programTarget, program) === 'duration';
            const isFrequencyTarget = getTargetType(programTarget, program) === 'frequency';
            const isPhantomTarget = isDurationTarget || isFrequencyTarget;
            const phantomTrials = isPhantomTarget
              ? chain(sessionProgramDataWithScoringData)
                .filter({ target: programTarget.id, phantomTrial: true })
                .value()
              : [];
            const trialsRangeEnd = isPhantomTarget
              ? Math.max(Number.isFinite(maxTrial) ? maxTrial : 0, phantomTrials.length)
              : maxTrial;

            trials = chain(range(1, trialsRangeEnd + 1))
              .map((trial) => chain(sessionProgramDataWithScoringData)
                .find({ target: programTarget.id, trial })
                .defaultTo({})
                .value())
              // For duration and frequency targets: show phantom trials when there are no recorded trials
              .thru((baseTrials) => {
                const hasAnyRealTrial = baseTrials.some((t) => !isEmpty(t));

                if (!isPhantomTarget || hasAnyRealTrial || phantomTrials.length === 0) {
                  return baseTrials;
                }

                return baseTrials.map((trial, index) => phantomTrials[index] || trial);
              })
              .concat(
                chain(sessionProgramDataWithScoringData)
                  .filter({ target: programTarget.id, durationFromSession: true })
                  .map((dataItem) => ({
                    ...dataItem,
                    duration: (programTarget.dataType === 'rate' && programTarget.timerType === 'session')
                      ? dataItem.duration
                      : (data.duration || dataItem.duration),
                  }))
                  .value(),
              )
              .value();
          }

          const minTrialsLimit = data.targetsInfo?.[programTarget.id]?.minTrials;
          const isBelowMinTrials = !!minTrialsLimit && trials.filter((t) => !isEmpty(t)).length < minTrialsLimit;

          return {
            id: programTarget.id,
            target: programTarget,
            parentTarget: programTarget.parentTarget,
            dataType: programTarget.dataType || null,
            timerType: programTarget.timerType || null,
            parentTargetDataType: chain(programTargets)
              .find({ id: programTarget.parentTarget })
              .get('dataType', null)
              .value(),
            targetLabel: programTarget.label,
            discriminativeStimulus: programTarget.discriminativeStimulus,
            promptLevelTemplate: programTarget.promptLevelTemplate || programPromptLevelTemplate,
            defaultPromptLevelTemplate: get(programTarget, 'defaultPromptLevelTemplate', null),
            aggregatedData: {},
            hasData: !chain(sessionProgramDataWithScoringData).find({ target: programTarget.id }).isUndefined().value(),
            trials,
            ...(!!minTrialsLimit && { isBelowMinTrials }),
            order: targetOrder,
            indent,
          };
        })
        .orderBy('order')
        .value();
      // Calculate aggregated data per target
      return chain(mappedData)
        .map((programTarget) => {
          let aggregatedData;
          let hasData = get(programTarget, 'hasData', false);
          if (!hasData) {
            aggregatedData = {
              trials: null,
              successes: null,
              percentage: null,
              duration: null,
              minDuration: null,
              maxDuration: null,
              avgDuration: null,
              successfulDuration: null,
              successfulMinDuration: null,
              successfulMaxDuration: null,
              successfulAvgDuration: null,
            };
          }
          const isIntervalParent = programTarget.dataType === 'interval' && !programTarget.parentTarget;
          if (['taskAnalysis', 'targetSet'].includes(programTarget.dataType) || isIntervalParent) {
            // Aggregate sub-targets to parent
            const trials = chain(mappedData)
              .filter({ parentTarget: programTarget.id })
              .flatMap('trials')
              .reject(isEmpty)
              .reject({ scoreValue: null })
              .size()
              .value();
            aggregatedData = {
              trials,
              successes: chain(mappedData)
                .filter({ parentTarget: programTarget.id })
                .flatMap('trials')
                .filter({ scoreValue: 100 })
                .size()
                .value(),
              percentage: (trials > 0)
                ? chain(mappedData)
                  .filter({ parentTarget: programTarget.id })
                  .flatMap('trials')
                  .sumBy('scoreValue')
                  .value()
                  / trials
                : null,
              duration: chain(mappedData)
                .filter({ parentTarget: programTarget.id })
                .flatMap('trials')
                .sumBy('duration')
                .defaultTo(0)
                .value(),
              successfulDuration: chain(mappedData)
                .filter({ parentTarget: programTarget.id })
                .flatMap('trials')
                .filter({ scoreValue: 100 })
                .sumBy('duration')
                .defaultTo(0)
                .value(),
              minDuration: chain(mappedData)
                .filter({ parentTarget: programTarget.id })
                .flatMap('trials')
                .minBy('duration')
                .get('duration')
                .defaultTo(0)
                .value(),
              successfulMinDuration: chain(mappedData)
                .filter({ parentTarget: programTarget.id })
                .flatMap('trials')
                .filter({ scoreValue: 100 })
                .minBy('duration')
                .get('duration')
                .defaultTo(0)
                .value(),
              maxDuration: chain(mappedData)
                .filter({ parentTarget: programTarget.id })
                .flatMap('trials')
                .maxBy('duration')
                .get('duration')
                .defaultTo(0)
                .value(),
              successfulMaxDuration: chain(mappedData)
                .filter({ parentTarget: programTarget.id })
                .flatMap('trials')
                .filter({ scoreValue: 100 })
                .maxBy('duration')
                .get('duration')
                .defaultTo(0)
                .value(),
              avgDuration: chain(mappedData)
                .filter({ parentTarget: programTarget.id })
                .flatMap('trials')
                .meanBy('duration')
                .defaultTo(0)
                .value(),
              successfulAvgDuration: chain(mappedData)
                .filter({ parentTarget: programTarget.id })
                .flatMap('trials')
                .filter({ scoreValue: 100 })
                .meanBy('duration')
                .defaultTo(0)
                .value(),
            };
            hasData = (aggregatedData.trials > 0);
          } else {
            const isTrialsArray = isArray(programTarget.trials);
            const targetTrials = isTrialsArray ? chain(programTarget.trials).flatten() : chain(programTarget.trials);
            let trials = targetTrials.reject(isEmpty).reject({ scoreValue: null }).value();
            let duration = 0;
            if (
              programTarget.dataType === 'frequency'
              || (programTarget.dataType === null && program.type === 'frequency')
            ) {
              duration = data.duration || 0;
            } else {
              duration = targetTrials.sumBy('duration').defaultTo(0).value();
            }
            if (isTrialsArray) {
              // A behavior occurrence recorded as a plain count has no prompt level, yet it is data all the same.
              trials = trials.filter((item) => item.trial && (item.promptLevel || isBehaviorOccurrence(item)));
            }
            aggregatedData = {
              trials: trials.length,
              successes: trials.filter(({ scoreValue }) => scoreValue === 100).length,
              // NOTE: "percentage" is directly used for CSV exports and badge in TrialOverview.vue, and in
              // formatDataResult method for weighted and ratings PLT; modifications will affect them.
              percentage: (trials.length > 0)
                ? trials.reduce((acc, item) => acc + (item.scoreValue || 0), 0) / trials.length : null,
              duration,
              successfulDuration: targetTrials
                .filter({ scoreValue: 100 })
                .sumBy('duration')
                .defaultTo(0)
                .value(),
              minDuration: targetTrials
                .minBy('duration')
                .get('duration')
                .defaultTo(0)
                .value(),
              successfulMinDuration: targetTrials
                .filter({ scoreValue: 100 })
                .minBy('duration')
                .get('duration')
                .defaultTo(0)
                .value(),
              maxDuration: targetTrials
                .maxBy('duration')
                .get('duration')
                .defaultTo(0)
                .value(),
              successfulMaxDuration: targetTrials
                .filter({ scoreValue: 100 })
                .maxBy('duration')
                .get('duration')
                .defaultTo(0)
                .value(),
              avgDuration: targetTrials
                .filter((trial) => (trial.duration ?? null) !== null)
                .meanBy('duration')
                .defaultTo(0)
                .value(),
              successfulAvgDuration: targetTrials
                .filter({ scoreValue: 100 })
                .meanBy('duration')
                .defaultTo(0)
                .value(),
            };
          }
          return {
            ...programTarget,
            hasData,
            aggregatedData,
          };
        })
        .value();
    }, // analyzeTargetData()

  },
};
